import {
    ImageCoords,
    ImageLayerState,
    LngLat,
} from '@features/map-layers-list/new-layer-file-uploader/model/types';

export function coordsToRect(coords: ImageCoords): ImageLayerState {
    const [A, B, C, D] = coords;

    const center: LngLat = [(A[0] + C[0]) / 2, (A[1] + C[1]) / 2];
    const scaleX = Math.max(1e-9, Math.cos((center[1] * Math.PI) / 180));

    const width = Math.hypot((B[0] - A[0]) * scaleX, B[1] - A[1]);
    const height = Math.hypot((D[0] - A[0]) * scaleX, D[1] - A[1]);
    const rotation = Math.atan2(B[1] - A[1], (B[0] - A[0]) * scaleX);

    return {
        center,
        width,
        height,
        rotation,
    };
}

export function rectToCoords(rect: ImageLayerState): ImageCoords {
    const { center, width, height, rotation } = rect;

    const hw = width / 2;
    const hh = height / 2;

    const local: ImageCoords = [
        [-hw, hh],
        [hw, hh],
        [hw, -hh],
        [-hw, -hh],
    ];

    return local.map(p => toWorld(p, center, rotation)) as ImageCoords;
}

export function handlesGeoJSON(coords: ImageCoords): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: coords.map((c, i) => ({
            type: 'Feature',
            properties: { index: i },
            geometry: {
                type: 'Point',
                coordinates: c,
            },
        })),
    };
}

export function toLocal(point: LngLat, center: LngLat, rotation: number): LngLat {
    const scaleX = Math.max(1e-9, Math.cos((center[1] * Math.PI) / 180));
    const dx = (point[0] - center[0]) * scaleX;
    const dy = point[1] - center[1];

    const cos = Math.cos(-rotation);
    const sin = Math.sin(-rotation);

    return [dx * cos - dy * sin, dx * sin + dy * cos];
}

export function toWorld(local: LngLat, center: LngLat, rotation: number): LngLat {
    const scaleX = Math.max(1e-9, Math.cos((center[1] * Math.PI) / 180));
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    const x = local[0] * cos - local[1] * sin;
    const y = local[0] * sin + local[1] * cos;

    return [center[0] + x / scaleX, center[1] + y];
}

export function resizeFromCorner(
    rect: ImageLayerState,
    handleIndex: number,
    startMouse: LngLat,
    curMouse: LngLat
): ImageLayerState {
    const { center, width, height, rotation } = rect;
    const aspect = width / height;

    const startLocal = toLocal(startMouse, center, rotation);
    const curLocal = toLocal(curMouse, center, rotation);

    const dx = curLocal[0] - startLocal[0];
    const dy = curLocal[1] - startLocal[1];

    const sx = handleIndex === 0 || handleIndex === 3 ? -1 : 1;
    const sy = handleIndex === 0 || handleIndex === 1 ? 1 : -1;

    let newWidth: number;
    let newHeight: number;

    if (Math.abs(dx) > Math.abs(dy)) {
        newWidth = Math.max(0.000001, width + dx * sx);
        newHeight = newWidth / aspect;
    } else {
        newHeight = Math.max(0.000001, height + dy * sy);
        newWidth = newHeight * aspect;
    }

    const deltaLocal: LngLat = [((newWidth - width) / 2) * sx, ((newHeight - height) / 2) * sy];

    const newCenter = toWorld(deltaLocal, center, rotation);

    return {
        center: newCenter,
        width: newWidth,
        height: newHeight,
        rotation,
    };
}

export function rotateFromCorner(
    rect: ImageLayerState,
    startMouse: LngLat,
    curMouse: LngLat
): ImageLayerState {
    const { center, rotation } = rect;

    const angleStart = Math.atan2(startMouse[1] - center[1], startMouse[0] - center[0]);

    const angleNow = Math.atan2(curMouse[1] - center[1], curMouse[0] - center[0]);

    return {
        ...rect,
        rotation: rotation + (angleNow - angleStart),
    };
}
